import { createSlice } from "@reduxjs/toolkit";

const initialState = {
    notes: [],
    candidateApplications: [],
    uniqueJobIds: [],
    candidateUser: null,
    loading: false,
    error: null,
    // Pagination (scroll-up = older notes). `notes` is newest-first, exactly as the
    // API returns it, so an older page is appended to the END of the array.
    // `nextPage` is the page number parsed out of the API's `next` link — null on
    // the last page. `hasMore` is just `nextPage != null`, kept as its own flag so
    // consumers read intent rather than re-deriving it.
    hasMore: false,
    loadingMore: false,
    nextPage: null,
    totalCount: null,
};

const notesSlice = createSlice({
    name: "notes",
    initialState,
    reducers: {
        setNotes: (state, action) => {
            state.notes = action.payload.notes || [];
            state.candidateApplications = action.payload.candidateApplications || [];
            state.uniqueJobIds = action.payload.uniqueJobIds || [];
            state.candidateUser = action.payload.candidateUser || null;
            // A fresh first page resets the pagination window.
            state.hasMore = Boolean(action.payload.hasMore);
            state.nextPage = action.payload.nextPage ?? null;
            state.totalCount = action.payload.totalCount ?? null;
        },
        // Older page: append (newest-first array → older notes go last) and drop
        // ids we already hold. The dedupe matters because a backend that ignores
        // limit/offset would otherwise duplicate the whole list on every scroll.
        appendOlderNotes: (state, action) => {
            const incoming = action.payload.notes || [];
            const seen = new Set(
                state.notes.map((n) => n?.id).filter((id) => id != null),
            );
            const fresh = incoming.filter((n) => n?.id == null || !seen.has(n.id));
            state.notes = state.notes.concat(fresh);
            // Nothing new arrived → the server is repeating itself, so stop rather
            // than loop forever on the same page.
            const keepGoing = Boolean(action.payload.hasMore) && fresh.length > 0;
            state.hasMore = keepGoing;
            state.nextPage = keepGoing ? (action.payload.nextPage ?? null) : null;
            if (action.payload.totalCount != null) {
                state.totalCount = action.payload.totalCount;
            }
        },
        setNotesLoading: (state, action) => {
            state.loading = action.payload;
        },
        setNotesLoadingMore: (state, action) => {
            state.loadingMore = action.payload;
        },
        setNotesError: (state, action) => {
            state.error = action.payload;
        },
    },
});

export const {
    setNotes,
    appendOlderNotes,
    setNotesLoading,
    setNotesLoadingMore,
    setNotesError,
} = notesSlice.actions;
export default notesSlice.reducer;
